import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Plus, Save, Trash2, X, Pencil } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function JobRolesAdmin({ onChanged }) {
  const [roles, setRoles] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState({}); // {oldName: draftName}
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get("/reference/all");
    setRoles([...(data.job_roles || [])].sort((a, b) => a.localeCompare(b)));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusy("create");
    try {
      await api.post("/job-roles", { name });
      toast.success(`Added "${name}"`);
      setNewName("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const save = async (oldName) => {
    const newRoleName = (editing[oldName] || "").trim();
    if (!newRoleName) { toast.error("Name is required"); return; }
    setBusy(oldName);
    try {
      await api.patch("/job-roles", { old_name: oldName, new_name: newRoleName });
      toast.success("Renamed — cascaded to all contractors");
      setEditing((e) => { const n = { ...e }; delete n[oldName]; return n; });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const remove = async (name) => {
    if (!window.confirm(`Remove job role "${name}"? It will also be removed from every contractor's profile.`)) return;
    setBusy(name);
    try {
      await api.delete(`/job-roles/${encodeURIComponent(name)}`);
      toast.success("Deleted");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="job-roles-admin">
      <div>
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#C0C0C0]" strokeWidth={2}/> Job roles
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          The list of job roles shown when creating or editing a contractor. Renaming a role
          updates every contractor who has it; deleting removes it from every profile.
        </p>
      </div>

      <div className="flex gap-2 items-end max-w-md">
        <div className="flex-1 space-y-1.5">
          <label className="pnc-eyebrow">Add a new job role</label>
          <Input
            data-testid="jr-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Detailer"
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            className="bg-[#1E1E1E] border-[#2A2A2A]"
          />
        </div>
        <Button data-testid="jr-create-btn" disabled={busy === "create"} onClick={create} className="gap-1.5 h-11 rounded-xl bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
          <Plus className="w-4 h-4" strokeWidth={2}/> Add
        </Button>
      </div>

      <div className="border border-[#2A2A2A] rounded-lg overflow-hidden bg-[#1E1E1E]">
        <Table data-testid="job-roles-table">
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.length === 0 && (
              <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-8">No job roles yet.</TableCell></TableRow>
            )}
            {roles.map((r) => {
              const isEditing = r in editing;
              return (
                <TableRow key={r} data-testid={`jr-row-${r}`}>
                  <TableCell className="font-medium">
                    {isEditing ? (
                      <Input
                        data-testid={`jr-edit-${r}`}
                        value={editing[r]}
                        onChange={(e) => setEditing({ ...editing, [r]: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") save(r); if (e.key === "Escape") setEditing((x) => { const n = { ...x }; delete n[r]; return n; }); }}
                        autoFocus
                        className="max-w-[300px] bg-[#141414] border-[#2A2A2A]"
                      />
                    ) : (
                      <span>{r}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {isEditing ? (
                      <>
                        <Button size="sm" data-testid={`jr-save-${r}`} onClick={() => save(r)} disabled={busy === r} className="gap-1.5 bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
                          <Save className="w-3.5 h-3.5"/> Save
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`jr-cancel-${r}`} onClick={() => setEditing((x) => { const n = { ...x }; delete n[r]; return n; })}>
                          <X className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" data-testid={`jr-edit-btn-${r}`} onClick={() => setEditing({ ...editing, [r]: r })} className="gap-1.5 border-[#2A2A2A] bg-[#141414] hover:bg-[#0A0A0A]">
                          <Pencil className="w-3.5 h-3.5"/> Rename
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`jr-del-${r}`} aria-label={`Delete ${r}`} onClick={() => remove(r)} disabled={busy === r} className="text-[#F87171] hover:bg-[#1F0A0A]">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
